import type { CSSProperties } from 'react';
import type { RpSection, RpSectionType } from '@mlm/shared';
import { cn } from '../lib/cn';
import { useRpCtx } from './context';
import { RpCta } from './sections/cta';
import { RpFeatureGrid } from './sections/feature-grid';
import { RpHero } from './sections/hero';
import { RpMediaText } from './sections/media-text';
import { RpButtonGroup, RpHeading, RpImage, RpRichText, RpSpacer, RpVideo } from './sections/basic';
import { RpCountdown } from './sections/countdown';
import { RpPackages } from './sections/packages';
import { RpFaq, RpGallery, RpLogoStrip, RpStats, RpSteps, RpTestimonials } from './sections/lists';
import { rpBg, rpOverlay, rpPad, rpTone, rpWidth } from './tokens';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Which component draws which section type.
 *
 * `Partial` on purpose, mirroring the storefront's `SECTION_REGISTRY`: a section
 * type that exists in the shared union but has no renderer yet (or one removed
 * in a later release) yields a dev warning and renders NOTHING, rather than
 * blowing up a public page. Forward- and backward-compatible by construction.
 */
const SECTION_REGISTRY: Partial<
  Record<RpSectionType, (props: { section: any }) => JSX.Element | null>
> = {
  hero: RpHero,
  cta: RpCta,
  mediaText: RpMediaText,
  featureGrid: RpFeatureGrid,
  heading: RpHeading,
  richText: RpRichText,
  image: RpImage,
  video: RpVideo,
  buttonGroup: RpButtonGroup,
  spacer: RpSpacer,
  packages: RpPackages,
  countdown: RpCountdown,
  steps: RpSteps,
  testimonials: RpTestimonials,
  faq: RpFaq,
  stats: RpStats,
  logoStrip: RpLogoStrip,
  gallery: RpGallery,
};

/**
 * Dev-only, without depending on the host's type setup.
 *
 * Not `process.env.NODE_ENV`: every SPA here pins `"types": ["vite/client"]`,
 * which deliberately excludes node types, so `process` doesn't typecheck in a
 * package shared across them. Not a bare `import.meta.env.DEV` either, since
 * that needs vite/client types which @mlm/ui doesn't (and shouldn't) pull in.
 * The cast reads the flag when a bundler provides it and stays quiet otherwise.
 */
function isDevBuild(): boolean {
  return (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
}

/** Render just a section's content, with no band wrapper (the `version: 0` path). */
export function RpSectionContent({ section }: { section: RpSection }) {
  const Component = SECTION_REGISTRY[section.type];
  if (!Component) {
    if (isDevBuild()) {
       
      console.warn(`[replicated] no renderer for section type "${section.type}"`);
    }
    return null;
  }
  return <Component section={section} />;
}

/**
 * One section, wrapped in its own full-width band.
 *
 * The band is what the pre-builder renderer could not express: it had a single
 * `max-w-2xl` column around every block, so a background could never reach the
 * viewport edge. Here each section owns its `<section>`, its background, and its
 * own inner width — including `width: 'full'`, which goes edge-to-edge.
 */
export function RpSectionBand({ section }: { section: RpSection }) {
  const ctx = useRpCtx();
  const style = section.style ?? {};
  const isImageBg = style.bg === 'image' && !!style.bgImageKey;

  const bandStyle: CSSProperties | undefined = isImageBg
    ? {
        backgroundImage: `url(${ctx.imageUrl(style.bgImageKey as string)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

  const selectable = ctx.mode === 'preview';
  const selected = selectable && ctx.selectedSectionId === section.id;

  return (
    <section
      // The id doubles as the `#anchor` target for `{kind:'anchor'}` links.
      id={section.id}
      data-rp-section-id={section.id}
      className={cn(
        'relative scroll-mt-4',
        rpBg(style.bg),
        rpTone(style.textTone),
        selectable &&
          'cursor-pointer outline-offset-[-2px] hover:outline hover:outline-2 hover:outline-primary/50',
        selected && 'outline outline-2 outline-primary',
      )}
      style={bandStyle}
      {...(selectable
        ? {
            onClick: (e: React.MouseEvent) => {
              // In the builder a click means "select this section", never
              // "follow this link" — otherwise editing a CTA navigates away.
              e.preventDefault();
              ctx.onSelectSection?.(section.id);
            },
          }
        : {})}
    >
      {isImageBg ? (
        <div aria-hidden className={cn('absolute inset-0', rpOverlay(style.overlay))} />
      ) : null}
      <div className={cn('relative', rpWidth(style.width), rpPad(style.padY))}>
        <RpSectionContent section={section} />
      </div>
    </section>
  );
}
